import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

// ─── TALENT ───────────────────────────────────────────────────────────────────
export function useTalent() {
  const { profile } = useAuth()
  const [talent, setTalent]   = useState([])
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(async () => {
    setLoading(true)
    let q = supabase.from('talent').select(`
      *, profiles:manager_id(id, name, email),
      pods(id, name, platform, members(id, status, violation_count, session_count))
    `).order('name')
    if (profile?.role === 'manager') q = q.eq('manager_id', profile.id)
    const { data } = await q
    setTalent(data || [])
    setLoading(false)
  }, [profile])

  useEffect(() => { fetch() }, [fetch])

  // Refetch when window regains focus — ensures compliance scores
  // are always fresh after session deletes/saves on other pages
  useEffect(() => {
    const onFocus = () => fetch()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [fetch])

  async function addTalent({ name, ig_handle, tt_handle, manager_id }) {
    const { error } = await supabase.from('talent').insert({ name, ig_handle, tt_handle, manager_id: manager_id || null, active: true })
    if (error) throw error
    await fetch()
  }

  async function assignManager(talentId, managerId) {
    const { error } = await supabase.from('talent').update({ manager_id: managerId || null }).eq('id', talentId)
    if (error) throw error
    await fetch()
  }

  async function updateTalent(id, updates) {
    const { error } = await supabase.from('talent').update(updates).eq('id', id)
    if (error) throw error
    await fetch()
  }

  return { talent, loading, refetch: fetch, addTalent, assignManager, updateTalent }
}

// ─── PODS ─────────────────────────────────────────────────────────────────────
export function usePods(talentId) {
  const [pods, setPods]       = useState([])
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(async () => {
    if (!talentId) { setPods([]); setLoading(false); return }
    setLoading(true)
    const { data } = await supabase.from('pods')
      .select('*, members(id, username, tier, violation_count, session_count, status, removed_at)')
      .eq('talent_id', talentId).order('name')
    setPods(data || [])
    setLoading(false)
  }, [talentId])

  useEffect(() => { fetch() }, [fetch])

  async function addPod({ name, platform, country = '', language = '' }) {
    const { error } = await supabase.from('pods').insert({ talent_id: talentId, name, platform, country, language })
    if (error) throw error
    await fetch()
  }

  async function deletePod(podId) {
    const { error } = await supabase.from('pods').delete().eq('id', podId)
    if (error) throw error
    await fetch()
  }

  async function renamePod(podId, updates) {
    // updates can include: name, platform, country, language
    const { error } = await supabase.from('pods').update(updates).eq('id', podId)
    if (error) throw error
    await fetch()
  }

  return { pods, loading, refetch: fetch, addPod, deletePod, renamePod }
}

// ─── MEMBERS (single pod) ─────────────────────────────────────────────────────
export function useMembers(podId) {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(async () => {
    if (!podId) { setMembers([]); setLoading(false); return }
    setLoading(true)
    const { data } = await supabase.from('members').select('*').eq('pod_id', podId).order('username')
    setMembers(data || [])
    setLoading(false)
  }, [podId])

  useEffect(() => { fetch() }, [fetch])

  async function addMember({ username, tier = 'a' }) {
    // Check if this username is blacklisted in this pod
    const normalized = username.replace(/^@/, '').toLowerCase()
    const { data: existing } = await supabase.from('members')
      .select('id, status, username')
      .eq('pod_id', podId)
      .ilike('username', '%' + normalized + '%')
    const blacklisted = (existing || []).find(m => 
      m.username.replace(/^@/, '').toLowerCase() === normalized &&
      (m.status === 'blacklisted' || m.status === 'banned')
    )
    if (blacklisted) {
      throw new Error(`@${normalized} is blacklisted in this pod and cannot be re-added without owner approval.`)
    }
    const { error } = await supabase.from('members').insert({ pod_id: podId, username, tier, status: 'active', violation_count: 0, session_count: 0 })
    if (error) throw error
    await fetch()
  }

  async function removeMember(memberId) {
    // Removed members go to blacklist — they cannot be re-added without owner approval
    const { error } = await supabase.from('members')
      .update({ status: 'blacklisted', removed_at: new Date().toISOString() })
      .eq('id', memberId)
    if (error) throw error
    await fetch()
  }

  async function approveRestore(memberId) {
    // Only callable by owner — restores a blacklisted member to active
    const { error } = await supabase.from('members')
      .update({ status: 'active', removed_at: null })
      .eq('id', memberId)
    if (error) throw error
    await fetch()
  }

  async function permanentBan(memberId) {
    // Owner permanently bans — keeps blacklisted but marks as permanent
    const { error } = await supabase.from('members')
      .update({ status: 'banned' })
      .eq('id', memberId)
    if (error) throw error
    await fetch()
  }

  return { members, loading, refetch: fetch, addMember, removeMember, approveRestore, permanentBan }
}

// ─── ALL MEMBERS (across all pods for one talent) ─────────────────────────────
export function useAllMembers(talentId) {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)

  const fetch = useCallback(async () => {
    if (!talentId) { setMembers([]); setLoading(false); return }
    setLoading(true)
    const { data } = await supabase.from('members')
      .select('*, pods!inner(id, name, platform, talent_id)')
      .eq('pods.talent_id', talentId)
      .eq('status', 'active')
    setMembers(data || [])
    setLoading(false)
  }, [talentId])

  useEffect(() => { fetch() }, [fetch])

  // Refetch members when window regains focus
  useEffect(() => {
    const onFocus = () => fetch()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [fetch])

  return { members, loading, refetch: fetch }
}

// ─── SESSIONS ─────────────────────────────────────────────────────────────────
export function useSessions(talentId) {
  const [sessions, setSessions] = useState([])
  const [loading, setLoading]   = useState(true)

  const fetch = useCallback(async () => {
    if (!talentId) { setSessions([]); setLoading(false); return }
    setLoading(true)
    const { data } = await supabase.from('sessions')
      .select(`*, session_pods(id, ok_count, violation_count, pods(id, name, platform)),
               violations(id, violation_type, members(id, username), pods(id, name, platform))`)
      .eq('talent_id', talentId)
      .order('created_at', { ascending: false })
    setSessions(data || [])
    setLoading(false)
  }, [talentId])

  useEffect(() => { fetch() }, [fetch])

  // Log a full session with violations.
  // If the same post_url was already run for this talent, we REPLACE it (undo old counts, apply new).
  async function createSession({ post_url, session_date, session_time, notes, pod_ids, commented, liked }) {

    // ── Check for duplicate post URL ──────────────────────────────────────────
    const { data: existing } = await supabase.from('sessions')
      .select('id')
      .eq('talent_id', talentId)
      .eq('post_url', post_url.trim())
      .limit(1)

    if (existing && existing.length > 0) {
      const existingId = existing[0].id

      // UNDO the old session's impact on member counts
      const { data: oldVios } = await supabase.from('violations')
        .select('member_id, violation_type')
        .eq('session_id', existingId)

      // Get all members touched by the old session
      const { data: oldPodLinks } = await supabase.from('session_pods')
        .select('pod_id').eq('session_id', existingId)
      const oldPodIds = (oldPodLinks || []).map(sp => sp.pod_id)

      for (const podId of oldPodIds) {
        const { data: mems } = await supabase.from('members').select('*').eq('pod_id', podId)
        for (const m of (mems || [])) {
          const memberOldVios = (oldVios || []).filter(v => v.member_id === m.id)
          const vioCount = memberOldVios.length
          // Undo: subtract session_count by 1 and violation_count by how many violations
          const newSC = Math.max(0, (m.session_count || 0) - 1)
          const newVC = Math.max(0, (m.violation_count || 0) - vioCount)
          await supabase.from('members').update({ session_count: newSC, violation_count: newVC }).eq('id', m.id)
        }
      }

      // Delete old violations, session_pods, and session record
      await supabase.from('violations').delete().eq('session_id', existingId)
      await supabase.from('session_pods').delete().eq('session_id', existingId)
      await supabase.from('sessions').delete().eq('id', existingId)
    }

    // ── Create fresh session ──────────────────────────────────────────────────
    const { data: session, error: sErr } = await supabase.from('sessions')
      .insert({ talent_id: talentId, post_url: post_url.trim(), session_date, session_time, notes })
      .select().single()
    if (sErr) throw sErr

    let grandOk = 0, grandVio = 0

    for (const podId of pod_ids) {
      const { data: members } = await supabase.from('members').select('*').eq('pod_id', podId).eq('status', 'active')
      const rows = members || []
      let podOk = 0, podVio = 0
      const violations = []

      for (const m of rows) {
        const key = m.username.replace(/^@/, '').toLowerCase()
        const didComment = commented.includes(key)
        const didLike    = liked.includes(key)
        let mVios = 0
        if (!didComment) { violations.push({ session_id: session.id, member_id: m.id, pod_id: podId, violation_type: 'no_comment' }); mVios++ }
        if (!didLike)    { violations.push({ session_id: session.id, member_id: m.id, pod_id: podId, violation_type: 'no_like'    }); mVios++ }
        if (mVios > 0) { podVio++; await supabase.from('members').update({ violation_count: (m.violation_count||0) + mVios, session_count: (m.session_count||0) + 1 }).eq('id', m.id) }
        else            { podOk++;  await supabase.from('members').update({ session_count: (m.session_count||0) + 1 }).eq('id', m.id) }
      }

      await supabase.from('session_pods').insert({ session_id: session.id, pod_id: podId, ok_count: podOk, violation_count: podVio })
      if (violations.length) await supabase.from('violations').insert(violations)
      grandOk += podOk; grandVio += podVio
    }

    await supabase.from('sessions').update({ total_ok: grandOk, total_violations: grandVio }).eq('id', session.id)
    await fetch()
    return session
  }

  async function deleteSession(sessionId) {
    // Step 1: Get all violations for this session
    // violations tell us exactly WHO was in the session and how many vios they got
    const { data: vios } = await supabase.from('violations')
      .select('member_id, violation_type')
      .eq('session_id', sessionId)

    // Step 2: Get which pods ran in this session
    const { data: podLinks } = await supabase.from('session_pods')
      .select('pod_id').eq('session_id', sessionId)
    const podIds = (podLinks || []).map(p => p.pod_id)

    // Step 3: Get ALL active members from those pods (these all got session_count +1 when session ran)
    const { data: allPodMembers } = await supabase.from('members')
      .select('id, session_count, violation_count, status')
      .in('pod_id', podIds.length ? podIds : ['00000000-0000-0000-0000-000000000000'])

    // Step 4: For each member, fetch their CURRENT counts fresh from DB then subtract
    for (const m of (allPodMembers || [])) {
      // Only undo for members who were active (participated in session)
      if (m.status !== 'active') continue

      // Get fresh counts directly from DB to avoid stale state
      const { data: fresh } = await supabase.from('members')
        .select('session_count, violation_count').eq('id', m.id).single()
      if (!fresh) continue

      const memberVioCount = (vios || []).filter(v => v.member_id === m.id).length
      const newSC = Math.max(0, (fresh.session_count || 0) - 1)
      const newVC = Math.max(0, (fresh.violation_count || 0) - memberVioCount)

      await supabase.from('members')
        .update({ session_count: newSC, violation_count: newVC })
        .eq('id', m.id)
    }

    // Step 5: Clean up session data
    await supabase.from('violations').delete().eq('session_id', sessionId)
    await supabase.from('session_pods').delete().eq('session_id', sessionId)
    await supabase.from('sessions').delete().eq('id', sessionId)

    // Step 6: Refresh sessions list
    await fetch()
  }

  return { sessions, loading, refetch: fetch, createSession, deleteSession }
}

// ─── VIOLATIONS ───────────────────────────────────────────────────────────────
export function useViolations(talentId) {
  const [violations, setViolations] = useState([])
  const [loading, setLoading]       = useState(true)

  useEffect(() => {
    if (!talentId) { setViolations([]); setLoading(false); return }
    ;(async () => {
      setLoading(true)
      const { data } = await supabase.from('violations')
        .select('*, members(id, username, violation_count, session_count), pods(id, name, platform, talent_id), sessions(id, talent_id, session_date)')
        .eq('pods.talent_id', talentId)
        .order('created_at', { ascending: false })
        .limit(300)
      setViolations(data || [])
      setLoading(false)
    })()
  }, [talentId])

  return { violations, loading }
}

// ─── MANAGERS ─────────────────────────────────────────────────────────────────
export function useManagers() {
  const [managers, setManagers] = useState([])
  const [loading, setLoading]   = useState(true)

  const fetch = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('profiles').select('*').eq('role', 'manager').order('name')
    setManagers(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { fetch() }, [fetch])

  async function toggleActive(managerId, active) {
    const { error } = await supabase.from('profiles').update({ active }).eq('id', managerId)
    if (error) throw error
    await fetch()
  }

  return { managers, loading, refetch: fetch, toggleActive }
}
